import { ReactNode } from "react";
import { cn } from "../../lib/utils";

/** Shared building blocks for the role-aware homes (Phase C). */

export const fmtMoney = (v: number, cur?: string | null) =>
  new Intl.NumberFormat("en-EN", { style: "currency", currency: cur || "EUR", maximumFractionDigits: 0 }).format(v || 0);
/** Ledger money: shown to the penny — a payment is matched and allocated exactly as it arrived. */
export const fmtMoneyExact = (v: number, cur?: string | null) =>
  new Intl.NumberFormat("en-EN", { style: "currency", currency: cur || "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);

export type CurrencyBucket = { currency: string | null; count: number; total: number };

export const Stat = ({ label, value, detail, tone }: { label: string; value: ReactNode; detail?: ReactNode; tone?: "good" | "bad" }) => (
  <div className="rounded-xl border bg-white p-4">
    <div className="text-sm text-slate-500">{label}</div>
    <div className="mt-1 text-2xl font-semibold">{value ?? "…"}</div>
    {detail != null && (
      <div className={cn("mt-0.5 text-xs", tone === "bad" ? "text-red-600" : tone === "good" ? "text-green-700" : "text-slate-400")}>
        {detail}
      </div>
    )}
  </div>
);

export const Panel = ({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) => (
  <div className="rounded-xl border bg-white p-4">
    <div className="mb-2 flex items-baseline justify-between">
      <h3 className="font-semibold">{title}</h3>
      {action}
    </div>
    {children}
  </div>
);

/** Per-currency totals as compact lines ("3 · €42,000"). */
export const BucketLines = ({ buckets, empty }: { buckets?: CurrencyBucket[]; empty: string }) => {
  if (!buckets) return <span className="text-slate-400">…</span>;
  if (!buckets.length) return <span className="text-slate-400">{empty}</span>;
  return (
    <span className="flex flex-col">
      {buckets.map((b) => (
        <span key={b.currency ?? "?"} className="tabular-nums">
          {b.count} · {fmtMoney(b.total, b.currency)}
        </span>
      ))}
    </span>
  );
};

export const FundedBadge = ({ funded }: { funded: boolean | null }) =>
  funded === null ? (
    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">No schedule</span>
  ) : funded ? (
    <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">Funded</span>
  ) : (
    <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">Balance due</span>
  );

export const DocsBadge = ({ docs }: { docs: "signed" | "sent" | "pending" }) =>
  docs === "signed" ? (
    <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">Signed</span>
  ) : docs === "sent" ? (
    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">Sent</span>
  ) : (
    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">Pending</span>
  );

export type DepartureRow = {
  id: string;
  startDate: string;
  daysTo: number;
  clientName: string | null;
  yachtName: string | null;
  brand: string | null;
  funded: boolean | null;
  docs: "signed" | "sent" | "pending";
};
